import React, { useState, useMemo, useRef } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, Card, CardHeader, CardBody, 
    Icon, Tag, Select, IconButton, Button, Badge, Input, InputGroup, InputRightElement
} from '@chakra-ui/react';
import { ChevronLeft, ChevronRight, Calendar, Truck, Search } from 'lucide-react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useThemeColors } from '@/hooks/useThemeColors';
import { useAccessCheck } from '@/hooks/useAccess';
import { useNavigate } from 'react-router-dom';

const TransportSummaryReportPage: React.FC = () => {
    const navigate = useNavigate();
    const colors = useThemeColors();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null); // 🌟 Added ref for search
    const access = useAccessCheck();

    const [filterType, setFilterType] = useState<'weekly' | 'monthly'>('weekly');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [searchQuery, setSearchQuery] = useState('');

    // --- 🌟 SEARCH HANDLERS ---
    const handleSearch = () => {
        setSearchQuery(searchRef.current?.value || '');
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    // --- 🌟 DATE NAVIGATION LOGIC ---
    const adjustDate = (direction: number) => {
        const newDate = new Date(currentDate);
        if (filterType === 'weekly') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setCurrentDate(newDate);
    };

    const dateRangeDisplay = useMemo(() => {
        const start = new Date(currentDate);
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1); 
            start.setDate(diff);
            const end = new Date(start);
            end.setDate(start.getDate() + 6);
            return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }, [currentDate, filterType]);

    const tableParams = useMemo(() => {
        const start = new Date(currentDate);
        const end = new Date(currentDate);
        
        if (filterType === 'weekly') {
            const day = start.getDay();
            const diff = start.getDate() - day + (day === 0 ? -6 : 1);
            start.setDate(diff);
            end.setDate(start.getDate() + 6);
        } else {
            start.setDate(1);
            end.setMonth(start.getMonth() + 1);
            end.setDate(0);
        }

        return { 
            filter_type: filterType, 
            start_date: start.toISOString().split('T')[0],
            end_date: end.toISOString().split('T')[0],
            search: searchQuery 
        };
    }, [filterType, currentDate, searchQuery]);

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataSource: '/reports/transport-summary', 
            primaryKey: 'truck_plate_number', 
            dataKey: 'data',
            showSelection: false,
            showCheckboxes: false,
            softDelete: false,
            displayInModal: false,
            showActionsColumn: false,
            noRecordText: 'No transport summary found for this period',
            pagination: true,
            defaultPageSize: 15,
            dataFields: {
                truck_plate_number: { name: 'TRUCK PLATE NUMBER', withSort: true },
                total_trips: { name: 'TOTAL TRIPS', withSort: true },
                total_expenses: { name: 'TOTAL EXPENSES', withSort: true },
                action_view: { name: 'ACTIONS', withSort: false },
            },
            actions: [],
            otherActions: [{ name: 'View Details', link: '/analytics/transport-summary' }]
        }),
        [access.can_view]
    );

    const customRenderers = useMemo(() => ({
        truck_plate_number: (val: string, row: any) => {
            const plate = val || row?.truck_plate_number; 
            return (
                <HStack 
                    spacing={3} 
                    onClick={() => {
                        const params = new URLSearchParams({
                            filter_type: filterType,
                            start_date: tableParams.start_date,
                            end_date: tableParams.end_date
                        });
                        navigate(`/analytics/transport-summary/${encodeURIComponent(plate)}?${params.toString()}`);
                    }}
                    cursor="pointer"
                >
                    <Icon as={Truck} size={14} color="blue.500" />
                    <Text fontWeight="bold" color="blue.600">{plate}</Text>
                </HStack>
            );
        },
        total_trips: (val: number) => (
            <Badge colorScheme={val > 10 ? "green" : "blue"} borderRadius="full" px={3}>
                {val} Trips
            </Badge>
        ),
        total_expenses: (val: number) => (
            <Text fontWeight="semibold">
                {new Intl.NumberFormat('en-PH', {
                    style: 'currency', currency: 'PHP', minimumFractionDigits: 2
                }).format(val || 0)}
            </Text>
        ),
        action_view: (_val: any, row: any) => (
            <Button
                size="xs" colorScheme="blue" variant="outline"
                leftIcon={<Icon as={Truck} size={12} />}
                onClick={() => {
                    const plate = row.truck_plate_number;
                    const params = new URLSearchParams({
                        filter_type: filterType,
                        start_date: tableParams.start_date,
                        end_date: tableParams.end_date
                    });
                    navigate(`/analytics/transport-summary/${plate}?${params.toString()}`);
                }}
            >
                View Details
            </Button>
        ),
    }), [filterType, tableParams, navigate]);

    return (
        <AdminLayout>
            <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
                    <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        <CardHeader flexShrink={0} py={3}>
                            <HStack justify="space-between" align="center">
                                <Box>
                                    <Heading size="md" mb={1}>Transport Summary Report</Heading>
                                    <Text fontSize="sm" color={colors.text}>Consolidated trip totals per truck plate number</Text>
                                </Box>
                                <Tag colorScheme="blue" variant="subtle" size="sm">{filterType.toUpperCase()} VIEW</Tag>
                            </HStack>
                        </CardHeader>

                        <CardHeader py={0} px={0} flexShrink={0}>
                            <VStack align="stretch" spacing={3} px={5} pb={3}>
                                <HStack justify="space-between" align="center" spacing={4} width="100%">
                                    <HStack spacing={4}>
                                        <Select 
                                            size="sm" w="140px" borderRadius="md" 
                                            value={filterType} 
                                            onChange={(e) => setFilterType(e.target.value as any)}
                                        >
                                            <option value="weekly">Weekly</option>
                                            <option value="monthly">Monthly</option>
                                        </Select>
                                        
                                        <HStack spacing={1}>
                                            <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
                                            <HStack 
                                                border="1px solid" borderColor="gray.200" px={4} py={1.5} borderRadius="md" 
                                                bg="white" cursor="pointer" position="relative" _hover={{ borderColor: "blue.400" }}
                                                onClick={() => (document.getElementById('report-date-picker') as HTMLInputElement)?.showPicker()}
                                            >
                                                <Icon as={Calendar} size={14} color="blue.500" />
                                                <Text fontWeight="bold" fontSize="sm" minW="150px" textAlign="center">{dateRangeDisplay}</Text>
                                                <input id="report-date-picker" type="date" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                                                    onChange={(e) => e.target.value && setCurrentDate(new Date(e.target.value))}
                                                />
                                            </HStack>
                                            <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
                                        </HStack>
                                    </HStack>
                                </HStack>

                                {/* 🌟 UPDATED SEARCH UI PATTERN */}
                                <Box flex="0 1 auto" minW="400px" maxW="400px">
                                    <InputGroup size="sm">
                                        <Input
                                            ref={searchRef}
                                            placeholder="Search trucks..."
                                            onKeyDown={handleKeyDown}
                                            defaultValue={searchQuery}
                                            pr="2.5rem"
                                        />
                                        <InputRightElement width="2.5rem">
                                            <IconButton
                                                aria-label="Search"
                                                icon={<Search size={16} />}
                                                size="sm"
                                                h="1.75rem"
                                                w="1.75rem"
                                                onClick={handleSearch}
                                                variant="ghost"
                                            />
                                        </InputRightElement>
                                    </InputGroup>
                                </Box>
                            </VStack>
                        </CardHeader>
               
                        <CardBody p={0} pt={1} pb={4} mx={5} flex="1" display="flex" flexDirection="column" minH={0} overflow="hidden">
                            <Box h="100%" display="flex" flexDirection="column" minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions} 
                                    params={tableParams}
                                    customRenderers={customRenderers}
                                    access={{ can_view: true, can_edit: false, can_create: false, can_delete: false }}
                                />
                            </Box>
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default TransportSummaryReportPage;